import React from "react";
import TemplatePicker from "./TemplatePicker";
import AnimationSelector from "./AnimationSelector";
import getDefaultMessage from "../utils/getDefaultMessage";
import templates from "../data/templates";
import { slugifyEventValue } from "../utils/eventLookup";

const effectOptions = [
  { value: "off", label: "Off" },
  { value: "subtle", label: "Subtle" },
  { value: "normal", label: "Normal" },
  { value: "big", label: "Big celebration" },
];

const slideshowOptions = [
  { value: "single", label: "Single featured photo" },
  { value: "three-photo", label: "3-photo collage" },
  { value: "photo-wall", label: "Busy photo wall" },
];

const qrOptions = [
  { value: "bottom-right", label: "Bottom right" },
  { value: "bottom-center", label: "Bottom center" },
  { value: "large-tv", label: "Large TV mode" },
  { value: "hidden", label: "Hidden" },
];

const messageOptions = [
  { value: "both", label: "Floating and under photos" },
  { value: "floating", label: "Floating background only" },
  { value: "under-photo", label: "Under photos only" },
  { value: "photo-only", label: "Photo only" },
];

const getDisplaySettings = (form) => ({
  accentColor: "#ec4899",
  effectIntensity: "normal",
  slideshowLayout: "three-photo",
  qrPosition: "bottom-right",
  messageStyle: "both",
  ...(form.displaySettings || {}),
});

const EventForm = ({
  form,
  setForm,
  onSubmit,
  onDelete,
  saving,
  lockDate = false,
}) => {
  const handleInput = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const displaySettings = getDisplaySettings(form);

  const updateDisplaySetting = (key, value) =>
    setForm((prev) => ({
      ...prev,
      displaySettings: {
        ...getDisplaySettings(prev),
        [key]: value,
      },
    }));

  const toggleAnimation = (type, value) =>
    setForm((prev) => ({
      ...prev,
      animations: { ...prev.animations, [type]: value },
    }));

  const eventTypes = [
    "birthday",
    "wedding",
    "graduation",
    "baby-shower",
    "farewell",
    "anniversary",
    "custom",
  ];

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block font-semibold mb-1">
          Who is the party for?
        </label>
        <input
          type="text"
          name="title"
          value={form.title}
          onChange={handleInput}
          className="w-full p-3 border border-pink-300 rounded-lg"
          required
        />
      </div>

      <div>
        <label className="block font-semibold mb-1">Date</label>
        <input
          type="date"
          name="date"
          value={form.date}
          onChange={handleInput}
          disabled={lockDate}
          className="w-full p-3 border border-pink-300 rounded-lg disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
          required
        />
        {lockDate && (
          <p className="mt-1 text-xs text-gray-500">
            The date is locked after the event has passed.
          </p>
        )}
      </div>

      <TemplatePicker
        selectedId={form.templateId}
        onChange={(id) =>
          setForm((prev) => {
            const template = templates.find((item) => item.id === id);
            return {
              ...prev,
              templateId: id,
              animations: template?.animations || prev.animations,
              displaySettings: {
                ...getDisplaySettings(prev),
                accentColor: template?.accentColor || "#ec4899",
              },
            };
          })
        }
      />

      {form.templateId && (
        <AnimationSelector
          animations={form.animations}
          onToggle={toggleAnimation}
        />
      )}

      <div className="rounded-lg border border-pink-100 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-lg font-bold text-gray-800">
          Display Settings
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block font-semibold mb-1">Accent Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={displaySettings.accentColor}
                onChange={(e) =>
                  updateDisplaySetting("accentColor", e.target.value)
                }
                className="h-11 w-16 cursor-pointer rounded border border-pink-200 bg-white p-1"
              />
              <input
                type="text"
                value={displaySettings.accentColor}
                onChange={(e) =>
                  updateDisplaySetting("accentColor", e.target.value)
                }
                className="w-full rounded-lg border border-pink-300 p-2"
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold mb-1">
              Effect Intensity
            </label>
            <select
              value={displaySettings.effectIntensity}
              onChange={(e) =>
                updateDisplaySetting("effectIntensity", e.target.value)
              }
              className="w-full rounded-lg border border-pink-300 p-3"
            >
              {effectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold mb-1">
              Slideshow Layout
            </label>
            <select
              value={displaySettings.slideshowLayout}
              onChange={(e) =>
                updateDisplaySetting("slideshowLayout", e.target.value)
              }
              className="w-full rounded-lg border border-pink-300 p-3"
            >
              {slideshowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold mb-1">QR Code Style</label>
            <select
              value={displaySettings.qrPosition}
              onChange={(e) =>
                updateDisplaySetting("qrPosition", e.target.value)
              }
              className="w-full rounded-lg border border-pink-300 p-3"
            >
              {qrOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block font-semibold mb-1">Message Style</label>
            <select
              value={displaySettings.messageStyle}
              onChange={(e) =>
                updateDisplaySetting("messageStyle", e.target.value)
              }
              className="w-full rounded-lg border border-pink-300 p-3"
            >
              {messageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="block font-semibold mb-1">Custom Event URL</label>
        <div className="flex rounded-lg border border-pink-300 bg-white focus-within:ring-2 focus-within:ring-pink-100">
          <span className="whitespace-nowrap border-r border-pink-200 px-3 py-3 text-sm text-gray-500">
            /event/
          </span>
          <input
            type="text"
            name="customSlug"
            value={form.customSlug || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                customSlug: slugifyEventValue(e.target.value),
              }))
            }
            placeholder="lilly-retirement"
            className="w-full rounded-r-lg p-3 outline-none"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Optional. Leave blank to use the generated event code.
        </p>
      </div>

      <div>
        <label className="block font-semibold mb-2">Event Type</label>
        <div className="flex flex-wrap gap-2">
          {eventTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  eventType: type,
                }))
              }
              className={`px-4 py-2 rounded-full border text-sm capitalize ${
                form.eventType === type
                  ? "bg-pink-500 text-white border-pink-600"
                  : "bg-white text-pink-500 border-pink-300"
              }`}
            >
              {type.replace("-", " ")}
            </button>
          ))}
        </div>
        {form.eventType && (
          <button
            type="button"
            className="mt-2 text-blue-500 underline text-sm"
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                welcomeMessage: getDefaultMessage(prev.eventType, prev.title),
              }))
            }
          >
            Use default message for {form.eventType}
          </button>
        )}
      </div>
      <div>
        <label className="block font-semibold mb-1">Welcome Message</label>
        <textarea
          name="welcomeMessage"
          value={form.welcomeMessage}
          onChange={handleInput}
          rows="4"
          className="w-full p-3 border border-pink-300 rounded-lg"
        />
      </div>

      <button
        type="submit"
        disabled={saving}
        className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold w-full"
      >
        {saving ? "Saving..." : "Save Changes"}
      </button>

      <button
        type="button"
        onClick={onDelete}
        className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold w-full mt-3"
      >
        Delete Event
      </button>
    </form>
  );
};

export default EventForm;
